import React from "react";
import Image from "components/AppImage";
import Icon from "components/AppIcon";

export default function OverviewTab({ opportunity }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Content */}
      <div className="lg:col-span-2 space-y-6">
        {/* Hero Image */}
        <div className="rounded-lg overflow-hidden h-48 md:h-64 w-full">
          <Image
            src={opportunity?.heroImage}
            alt={opportunity?.heroImageAlt}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Executive Summary */}
        <div className="card">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-3">Executive Summary</h3>
          <p className="font-body text-sm md:text-base text-muted-foreground leading-relaxed">{opportunity?.summary}</p>
        </div>

        {/* Project Timeline */}
        <div className="card">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Project Timeline</h3>
          <div className="space-y-3">
            {opportunity?.timeline?.map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-3 h-3 rounded-full mt-1.5 ${item?.completed ? "bg-success" : item?.active ? "bg-primary" : "bg-muted"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className={`font-caption text-sm font-medium ${item?.active ? "text-primary" : "text-foreground"}`}>{item?.milestone}</p>
                    <span className="font-data text-xs text-muted-foreground whitespace-nowrap">{item?.date}</span>
                  </div>
                  {item?.description && <p className="font-caption text-xs text-muted-foreground mt-0.5">{item?.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Highlights */}
        <div className="card">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-4">Investment Highlights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {opportunity?.highlights?.map((h, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-md bg-muted/50">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                <p className="font-caption text-sm text-foreground">{h}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Sidebar */}
      <div className="space-y-5">
        {/* Sponsor Info */}
        <div className="card">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3">Sponsor</h3>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0">
              <Image src={opportunity?.sponsor?.logo} alt={opportunity?.sponsor?.logoAlt} className="w-full h-full object-cover" />
            </div>
            <div>
              <p className="font-caption font-semibold text-foreground text-sm">{opportunity?.sponsor?.name}</p>
              <p className="font-caption text-xs text-muted-foreground">{opportunity?.sponsor?.type}</p>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            {[
              { label: "Track Record", value: opportunity?.sponsor?.trackRecord },
              { label: "AUM", value: opportunity?.sponsor?.aum },
              { label: "HQ", value: opportunity?.sponsor?.hq },
            ]?.map((item) => (
              <div key={item?.label} className="flex justify-between">
                <span className="font-caption text-muted-foreground">{item?.label}</span>
                <span className="font-caption font-medium text-foreground">{item?.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Deal Metrics */}
        <div className="card">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3">Deal Metrics</h3>
          <div className="space-y-3">
            {opportunity?.dealMetrics?.map((m) => (
              <div key={m?.label} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                <span className="font-caption text-sm text-muted-foreground">{m?.label}</span>
                <span className="font-data text-sm font-medium text-foreground">{m?.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Co-investors */}
        <div className="card">
          <h3 className="font-heading text-base font-semibold text-foreground mb-3">Co-Investors</h3>
          <div className="space-y-2">
            {opportunity?.coInvestors?.map((ci, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Building2" size={12} color="var(--color-primary)" />
                </div>
                <span className="font-caption text-sm text-foreground">{ci}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}